import { FC, useCallback, useEffect, useState } from "react";
import { DialogContent, DialogTitle, StepLabel, Step, Stepper, Grid, StepContent, Button, IconButton, DialogActions, CircularProgress } from "@mui/material";
import { Dialog } from "@mui/material";
import { LineItemDto, MaterialLineItemDto, OrderReviewDto, OrderReviewLineItemDto } from "../../../../dtos";
import { OrderReviewSelectList } from "./OrderReviewSelectList";
import { OrderReviewLineItemSelectList } from "./OrderReviewLineItemSelectList";
import { CancelOutlined } from "@mui/icons-material";
import theme from "../../../../Theme";
import { QuoteLineItemSelectList } from "../QuoteLineItemSelectList";
import { useLineItemConverter } from "../../../../util";
import { useCreateOrderReviewAddLineItemToOrderReviewMutation, useGetMaterialQuery, useGetOrderReviewLineItemsQuery } from "../../../../store/generated/generatedApi";
import { LineItemTypes, QuoteType } from "../../../../util";
import { useMaterialDuplicateChecker } from "../../../../util/useMaterialDuplicateChecker";
import { QuickAddMaterialLineItemDialogForOrderReview } from "../QuickAddMaterialLineItemDialogForOrderReview";

export interface ISelectOrderReviewLineItemStepperDialog {
    open: boolean;
    onClose: () => void;
    onComplete: (orderReview: OrderReviewDto, lineItem: OrderReviewLineItemDto) => void;
    date: Date;
}

export const SelectOrderReviewLineItemStepperDialog: FC<ISelectOrderReviewLineItemStepperDialog> = (props) => {
    const { open, onClose, onComplete, date } = props;

    const [orderReview, setOrderReview] = useState<OrderReviewDto | null>(null);
    const [activeStep, setActiveStep] = useState(0);
    const [isMaterialSelectionDialogOpen, setIsMaterialSelectionDialogOpen] = useState(false);
    const [selectedHaulingLineItem, setSelectedHaulingLineItem] = useState<LineItemDto | null>(null);
    const { createNewOrderReviewLineItemFromQuoteLineItem, convertQuoteLineItemToOrderReviewLineItem } = useLineItemConverter(orderReview?.quote);

    const [createOrderReviewLineItem, { data, isLoading: isCreating, isSuccess, reset }] = useCreateOrderReviewAddLineItemToOrderReviewMutation();

    // Get materials to look up materialId by code (since materialId is not always in the lineItem structure)
    const { data: materialsData } = useGetMaterialQuery({
        sorting: ['CODE+'],
        page: 0,
        pageSize: 10000,
        includeInactive: false
    });

    // Get existing order review line items for duplicate checking
    const { data: orderReviewLineItemsData } = useGetOrderReviewLineItemsQuery(
        { orderReviewId: orderReview?.id ?? '' },
        { skip: !orderReview?.id }
    );

    const handleSelectionComplete = useCallback((orderReviewLineItem) => {
        onComplete(orderReview!, orderReviewLineItem);
        onClose();
        setActiveStep(0);
        setOrderReview(null);
        setIsMaterialSelectionDialogOpen(false);
        setSelectedHaulingLineItem(null);
        reset();
    }, [onComplete, onClose, reset, orderReview]);

    useEffect(() => {
        if (isSuccess) {
            // Close material dialog if it's open
            if (isMaterialSelectionDialogOpen) {
                setIsMaterialSelectionDialogOpen(false);
                setSelectedHaulingLineItem(null);
            }
            handleSelectionComplete(data);
        }
    }, [isSuccess, data, handleSelectionComplete, isMaterialSelectionDialogOpen]);

    // Use reusable hook for duplicate checking
    const { checkForDuplicateMaterial, getExcludedMaterialIdsForHaulingLineItem } = useMaterialDuplicateChecker({
        lineItems: orderReviewLineItemsData?.pageResults ?? [],
        materialsData,
    });

    // Handle material line item creation
    const handleAddMaterialLineItem = useCallback(
        async (materialLineItem: MaterialLineItemDto) => {
            if (!orderReview) {
                return;
            }

            // Convert MaterialLineItemDto to OrderReviewLineItemDto
            const materialOrderReviewLineItem = await convertQuoteLineItemToOrderReviewLineItem(
                materialLineItem as LineItemDto,
                (orderReviewLineItemsData?.pageResults?.length ?? 0) + 1,
                orderReview
            );

            // Add to order review via API
            createOrderReviewLineItem(materialOrderReviewLineItem);
        },
        [orderReview, convertQuoteLineItemToOrderReviewLineItem, orderReviewLineItemsData, createOrderReviewLineItem]
    );

    const handleAddNewOrderReviewLineItem = useCallback(
        async (quoteLineItem?: LineItemDto) => {
            if (orderReview && quoteLineItem) {
                // Check if it's a SiteHauling line item and quote is Standard
                if (quoteLineItem.type === LineItemTypes.SiteHauling && orderReview.quote?.type === QuoteType.Standard) {
                    // Open material selection dialog instead of directly creating the order review line item
                    setSelectedHaulingLineItem(quoteLineItem);
                    setIsMaterialSelectionDialogOpen(true);
                } else {
                    // For non-hauling line items or non-standard quotes, create directly
                    var newOrderReviewLineItem = await createNewOrderReviewLineItemFromQuoteLineItem(quoteLineItem, orderReview);
                    createOrderReviewLineItem(newOrderReviewLineItem);
                }
            }
        },
        [createNewOrderReviewLineItemFromQuoteLineItem, createOrderReviewLineItem, orderReview]
    );

    // Reset state when dialog closes
    useEffect(() => {
        if (!open) {
            setIsMaterialSelectionDialogOpen(false);
            setSelectedHaulingLineItem(null);
        }
    }, [open]);

    return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
        <DialogTitle>
            <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>Select Order Review Line Item</Grid>
                <Grid item>
                    <IconButton onClick={onClose} size="small" sx={{ color: theme.palette.primary.contrastText }}>
                        <CancelOutlined />
                    </IconButton>
                </Grid>
            </Grid>
        </DialogTitle>
        <DialogContent>
            <Grid container pt={2} spacing={2} direction='column' height='100%'>
                <Grid item height='100%'>
                    <Stepper
                        activeStep={activeStep}
                        orientation="vertical"
                        >
                        <Step key={'order-review'}>
                            <StepLabel>Select Order Review</StepLabel>
                            <StepContent>
                                <OrderReviewSelectList 
                                    date={date} 
                                    onSelect={(orderReview) => {
                                        setActiveStep(1);
                                        setOrderReview(orderReview);
                                    }} />
                            </StepContent>
                        </Step>
                        <Step key={'line-item'}>
                            <StepLabel>Select Line Item</StepLabel>
                            <StepContent>
                                <Grid container direction='column' spacing={2}>
                                    <Grid item>
                                        {orderReview && 
                                        <OrderReviewLineItemSelectList 
                                            orderReviewId={orderReview.id} 
                                            onSelect={handleSelectionComplete}
                                            onAddNewLineItem={() => {
                                                setActiveStep(2);
                                            }}
                                        /> }
                                    </Grid>
                                </Grid>
                            </StepContent>
                        </Step>
                        <Step key={'new-line-item'}>
                            <StepLabel>Add New Line Item</StepLabel>
                            <StepContent>
                                <Grid container direction='column' spacing={2}> 
                                    <Grid item>
                                        {orderReview && <QuoteLineItemSelectList
                                            quote={orderReview!.quote!}
                                            disableAddCustomLineItem={true}
                                            onAddQuoteLineItem={(quoteLineItem) => {
                                                handleAddNewOrderReviewLineItem(quoteLineItem);
                                            }}
                                            onAddCustomLineItem={() => {}}
                                        />}
                                    </Grid>
                                </Grid>
                            </StepContent>
                        </Step>
                    </Stepper>
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            {isCreating && <CircularProgress />}
            {activeStep > 0 && <Button disabled={isCreating} variant='contained' onClick={() => {
                setActiveStep(activeStep - 1);
            }}>Back</Button>}
            {activeStep === 0 && <Button disabled={isCreating} variant='contained' onClick={onClose}>Close</Button>}
        </DialogActions>
        {selectedHaulingLineItem && (
            <QuickAddMaterialLineItemDialogForOrderReview
                open={isMaterialSelectionDialogOpen}
                onClose={() => {
                    setIsMaterialSelectionDialogOpen(false);
                    setSelectedHaulingLineItem(null);
                }}
                onAdd={handleAddMaterialLineItem}
                siteHaulingLineItem={selectedHaulingLineItem}
                checkForDuplicateMaterial={checkForDuplicateMaterial}
                getExcludedMaterialIdsForHaulingLineItem={getExcludedMaterialIdsForHaulingLineItem}
                customerName={orderReview?.quote?.customer?.name}
            />
        )}
    </Dialog>
    );
}

